/**
 * The composer's SEND LIFECYCLE, held above the composer (CONAPP-530).
 *
 * ⛔ IT IS HERE FOR THE SAME REASON `draftsByThread` IS — LIFETIME, not reach. A send outlives the
 * component that started it: `ProviderMessagesPanel` swaps `MessageReplyContainer` for the closed
 * notice on a conversation that cannot be replied to, and the app's tab bar unmounts the WHOLE PANEL
 * when the member looks at their Carrot Companion conversations. Either one destroys a guard held in
 * the composer, and the composer that comes back has a FRESH one — so it offers Send for a note that
 * is still out, and a second tap posts it twice. `createNote` has no idempotency key and a note
 * attributed to the member cannot be deleted with our credential, so the duplicate is permanent.
 *
 * ⛔ AND IT IS OBSERVABLE RATHER THAN A BARE SET, which is the half a ref cannot do. The send that
 * has to re-enable the composer finishes inside a closure belonging to an UNMOUNTED one, so the
 * composer on screen learns about it only by subscribing: this is a `useSyncExternalStore` store, and
 * `announce` is what reaches the live instance.
 *
 * ⚠ MOUNT-SCOPED, NEVER A MODULE CONSTANT. The host builds one per `MessagesShell` mount (see
 * src/pages/messages-shell.tsx) so nothing here survives a sign-out.
 */
export type MessageSendStore = {
  /** React's `useSyncExternalStore` subscription. Returns the unsubscribe. */
  subscribe: (onStoreChange: () => void) => () => void
  /**
   * Whether a send is out ANYWHERE, ⛔ deliberately not per conversation.
   *
   * It is the composer's one gate: the submit guard asks it synchronously and every control that
   * communicates the hold renders from it, so the two cannot drift (CONAPP-530). Scoping it per
   * conversation would leave Send live in the conversation the member moved to while the guard still
   * refused it — a control that looks ready and posts nothing, which is the defect this shape exists
   * to close.
   */
  isSending: () => boolean
  /**
   * Whether the send that is out belongs to THIS conversation.
   *
   * ⛔ FOR SAYING SO, NEVER FOR GATING (CONAPP-568). `isSending` above is the gate and stays the
   * gate — one send at a time is the invariant the composer enforces, and narrowing it would put a
   * live Send in front of a member whose first note is still in flight. What this narrows is the
   * SENTENCE: "Sending your message." was announced in every conversation, so a member who started a
   * send in A and moved to B was told B's composer was sending a message it had never been given.
   * The controls in B really are held; the claim about what they are held FOR was the wrong one.
   */
  isSendingFrom: (threadId: string) => boolean
  /**
   * How many sends have POSTED in this conversation. Monotonic, and only ever compared against
   * itself: an increase is what tells whichever composer holds this conversation's box that the words
   * in it have gone, whether or not it is the composer that sent them.
   *
   * ⚠ A COUNT AND NOT A FLAG, so it cannot be left set. A flag written while no composer is showing
   * the conversation would still be standing when the member next types there.
   */
  postedCount: (threadId: string) => number
  /** ⛔ Called synchronously, ahead of the send's first await — see the guard in `onSubmit`. */
  begin: (threadId: string) => void
  /**
   * `posted` — the note reached the transport. Its words must come out of the box; a send that FAILED
   * leaves them there, because the member has to be able to retry.
   */
  end: (threadId: string, posted: boolean) => void
}

export function createMessageSendStore(): MessageSendStore {
  // A SET rather than a boolean, so `end` clears the conversation it belongs to and not whatever
  // happened to be last. Only one send is ever out at a time today — `isSending` is what holds the
  // second — but a boolean would make that an invariant this file depends on rather than one the
  // composer enforces.
  const inFlight = new Set<string>()
  const posted = new Map<string, number>()
  const listeners = new Set<() => void>()

  function announce(): void {
    listeners.forEach((listener) => listener())
  }

  return {
    subscribe: (onStoreChange) => {
      listeners.add(onStoreChange)
      return () => {
        listeners.delete(onStoreChange)
      }
    },
    isSending: () => inFlight.size > 0,
    isSendingFrom: (threadId) => inFlight.has(threadId),
    postedCount: (threadId) => posted.get(threadId) ?? 0,
    begin: (threadId) => {
      inFlight.add(threadId)
      announce()
    },
    end: (threadId, wasPosted) => {
      inFlight.delete(threadId)
      if (wasPosted) posted.set(threadId, (posted.get(threadId) ?? 0) + 1)
      announce()
    }
  }
}
